
import { useState, useMemo } from "react";
import { BirdieGameGroup } from "@/types";
import { toast } from "@/hooks/use-toast";
import { getRecurringGroups } from "@/integrations/supabase/game/gameQueryService";
import GameCard from "./game/GameCard";
import RecurringGameCard from "./game/RecurringGameCard";
import GameListSkeleton from "./game/GameListSkeleton";
import EmptyGamesList from "./game/EmptyGamesList";
import ArchivedGamesToggle from "./game/ArchivedGamesToggle";
import CreateGameButton from "./game/CreateGameButton";
import { useNavigate } from "react-router-dom";

interface GamesListProps {
  games: BirdieGameGroup[];
  onSelectGame: (game: BirdieGameGroup) => void;
  onCreateGame: () => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

const GamesList = ({
  games,
  onSelectGame,
  onCreateGame,
  onRefresh,
  isLoading = false
}: GamesListProps) => {
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  // Toggle archived games visibility
  const toggleArchived = () => {
    setShowArchived(prev => !prev);
    toast({
      title: showArchived ? "Completed games hidden" : "Showing completed games",
      description: showArchived ? "Displaying active games only" : "Including completed games in list"
    });
  };

  // Handle clicking a game card
  const handleGameClick = (game: BirdieGameGroup) => {
    // Call the provided onSelectGame handler
    onSelectGame(game);
    
    // Navigate to the game detail page for this game
    navigate(`/games/${game.id}`);
  };

  // Split into recurring groups and standalone games
  const { recurringGroups, standaloneGames: standalone } = useMemo(
    () => getRecurringGroups(games),
    [games]
  );

  // Filter standalone games based on archive status, newest first
  const filteredStandalone = standalone
    .filter(game => showArchived || !game.isArchived)
    .sort((a, b) => b.year - a.year);

  // Filter recurring groups: hide if all instances are archived (unless showArchived)
  const filteredRecurringGroups = recurringGroups.filter(group =>
    showArchived || group.instances.some(i => !i.isArchived)
  );

  const isAnyGameArchived = games.some(game => game.isArchived);
  const hasVisibleContent = filteredStandalone.length > 0 || filteredRecurringGroups.length > 0;

  return (
    <div className="px-4 pb-20">
      <CreateGameButton onClick={onCreateGame} />
      
      {isLoading ? (
        <GameListSkeleton />
      ) : !hasVisibleContent ? (
        <EmptyGamesList />
      ) : (
        <div className="space-y-6">
          {/* Recurring game groups */}
          {filteredRecurringGroups.map((group) => (
            <RecurringGameCard
              key={group.recurringGroupId}
              group={group}
              courseName={group.instances[0]?.courseName || "Unknown Course"}
              onRefresh={onRefresh}
            />
          ))}

          {/* Standalone games */}
          {filteredStandalone.map((game) => (
            <GameCard
              key={game.id}
              game={game}
              courseName={game.courseName || "Unknown Course"}
              onClick={() => handleGameClick(game)}
            />
          ))}

          {/* Show/Hide Archived Link - only display if there are archived games */}
          {isAnyGameArchived && (
            <ArchivedGamesToggle
              showArchived={showArchived}
              onToggle={toggleArchived}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GamesList;
